import React, { useMemo, useState } from 'react';
import { Accordion, Badge, Card, Spinner } from 'react-bootstrap';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import * as Lo from 'lodash';
import { FaChevronDown } from 'react-icons/fa';

import { CollectionOrderStates } from 'shared-pages/shared/constants';
import {
  CollectionOrdersStatusesTable,
  CollectionOrderStatusRow,
} from './collection-orders-statuses-table';
import { gqlGetCollectionOrdersForStatuses } from './collection-orders-statuses-graphql';
import {
  buildWhereFromFilterValues,
  CollectionOrdersStatusesFilterValues,
  CollectionOrdersStatusesFilters,
  getDefaultLastYearRange,
} from './collection-orders-statuses-filters';
import './collection-orders-statuses.css';

const FETCH_LIMIT = 10000;

/** Σειρά εμφάνισης καταστάσεων (enum στο TS δεν εγγυάται σειρά τιμών). */
const STATUS_DISPLAY_ORDER: CollectionOrderStates[] = [
  CollectionOrderStates.TemporarySaved,
  // CollectionOrderStates.NotRouted,
  // CollectionOrderStates.Routed,
  CollectionOrderStates.ApplicationSubmitted,
  CollectionOrderStates.OrganizationCompleted,
  CollectionOrderStates.PackageShipmentCompleted,
  CollectionOrderStates.CollectorOrder,
  CollectionOrderStates.ScheduledCollectorCollection,
  CollectionOrderStates.Executed,
  CollectionOrderStates.ApplicationRejected,
];

/** Όταν δεν υπάρχουν γραμμές, δεν έρχεται όνομα από το API — χρησιμοποιούμε σταθερές ετικέτες. */
const STATUS_LABEL_EL: Record<number, string> = {
  [CollectionOrderStates.TemporarySaved]: 'Προσωρινή αποθήκευση',
  [CollectionOrderStates.NotRouted]: 'Μη δρομολογημένη',
  [CollectionOrderStates.Routed]: 'Δρομολογημένη',
  [CollectionOrderStates.Executed]: 'Ολοκληρωμένη',
  [CollectionOrderStates.ApplicationSubmitted]: 'Υποβολή αίτησης',
  [CollectionOrderStates.ApplicationRejected]: 'Η αίτηση απορρίφθηκε',
  [CollectionOrderStates.OrganizationCompleted]: 'Ολοκλήρωση οργάνωσης',
  [CollectionOrderStates.PackageShipmentCompleted]: 'Ολοκλήρωση αποστολής συσκευασιών',
  [CollectionOrderStates.CollectorOrder]: 'Εντολή συλλέκτη',
  [CollectionOrderStates.ScheduledCollectorCollection]: 'Προγραμματισμένη συλλογή συλλέκτη',
};

export const CollectionordersStatusesPage = () => {
  const { t } = useTranslation();
  const defaultRange = useMemo(() => getDefaultLastYearRange(), []);
  const [filters, setFilters] = useState<CollectionOrdersStatusesFilterValues>({
    searchText: '',
    collectorId: undefined,
    isActive: true,
    regionId: undefined,
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    statusId: [],
  });

  const where = useMemo(() => buildWhereFromFilterValues(filters), [filters]);

  const { loading, error, data } = useQuery(gqlGetCollectionOrdersForStatuses, {
    variables: {
      limit: FETCH_LIMIT,
      offset: 0,
      order: 'statusId ASC, orderedDate DESC',
      where,
    },
    fetchPolicy: 'network-only',
  });

  const payload = data?.getCollectionOrders;
  const rows: CollectionOrderStatusRow[] = payload?.data ?? [];
  const recordCount: number | undefined = payload?.recordCount;

  const grouped = useMemo(() => Lo.groupBy(rows, (r) => r.statusId), [rows]);

  const unknownStatuses = useMemo(() => {
    const known = new Set(STATUS_DISPLAY_ORDER.map(Number));
    return Lo.uniq(rows.map((r) => r.statusId).filter((id) => !known.has(id))).sort(
      (a, b) => a - b,
    );
  }, [rows]);

  if (error) {
    return (
      <Card className="shadow">
        <Card.Body>
          <p className="text-danger mb-0">{error.message}</p>
        </Card.Body>
      </Card>
    );
  }

  const title = t('Αιτήσεις Συλλογής ανά Κατάσταση');

  return (
    <Card className="shadow">
      <Card.Header>
        <Card.Title className="m-0">{title}</Card.Title>
      </Card.Header>
      <Card.Body>
        <CollectionOrdersStatusesFilters
          initialValues={filters}
          onChange={(values) => setFilters(values)}
          disable={loading}
        />

        {loading && (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        )}

        {!loading && recordCount != null && recordCount > rows.length && (
          <p className="text-warning small">
            {t(
              'Εμφανίζονται οι πρώτες {{shown}} από {{total}} εγγραφές. Φιλτράρετε από τη λίστα αιτήσεων για πλήρη λίστα.',
              { shown: rows.length, total: recordCount },
            )}
          </p>
        )}

        {!loading && (
          <Accordion alwaysOpen className="status-accordion">
            {[...STATUS_DISPLAY_ORDER, ...unknownStatuses].map((statusId) => {
              const list = grouped[statusId] ?? [];
              const label =
                list[0]?.collectionOrderState?.name ??
                STATUS_LABEL_EL[statusId] ??
                `${t('Κατάσταση')} ${statusId}`;

              return (
                <Accordion.Item eventKey={String(statusId)} key={statusId}>
                  <Accordion.Header>
                    <div className="status-accordion-header-content">
                      <div className="status-accordion-title-group">
                        <span className="status-accordion-title">{label}</span>
                        <Badge pill className="status-count-badge">
                          {list.length}
                        </Badge>
                      </div>
                      <FaChevronDown className="status-accordion-chevron" aria-hidden />
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    {list.length === 0 ? (
                      <div className="status-empty-state">{t('Δεν υπάρχουν αιτήσεις.')}</div>
                    ) : (
                      <div className="status-details-panel">
                        <CollectionOrdersStatusesTable rows={list} />
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </Card.Body>
    </Card>
  );
};
